import React from 'react';
import PropTypes from 'prop-types';

// Target System Forms
import { ApiKeyForm as AmazonBedrockApiKeyForm } from './amazon-bedrock';
import { OAuth2Form as AzureOpenAiOAuth2Form } from './azure-openai';
import { ServiceAccountForm as VertexAiServiceAccountForm } from './vertex-ai';
import { SsoKeyForm as GoDaddyAnsSsoKeyForm } from './godaddy-ans';

/**
 * Form registry mapping targetSystem:authMethod to their form components.
 *
 * This registry allows adding new target systems without modifying the router logic.
 * Just add a new entry with the appropriate key and component.
 */
const FORM_REGISTRY = {
  // Amazon Bedrock
  'amazon-bedrock:access-key': AmazonBedrockApiKeyForm,

  // Microsoft Azure Copilot
  'azure-openai:oauth': AzureOpenAiOAuth2Form,

  // Google Vertex AI
  'vertex-ai:service-account': VertexAiServiceAccountForm,

  // GoDaddy ANS
  'godaddy-ans:sso-key': GoDaddyAnsSsoKeyForm
};

const isScenarioEnabled = (scenarioKey, isAgentScannerGoDaddyEnabled) => {
  if (scenarioKey === 'godaddy-ans:sso-key') {
    return Boolean(isAgentScannerGoDaddyEnabled);
  }

  return true;
};

/**
 * AuthenticationFieldsRouter
 *
 * Router component that delegates to the appropriate authentication form
 * based on the selected target system and authentication method.
 *
 * This follows the Strategy Pattern - each target system has its own
 * form component that knows how to render its specific fields.
 *
 * @example
 * <AuthenticationFieldsRouter
 *   targetSystem="amazon-bedrock"
 *   authMethod="access-key"
 *   formData={{ accessKeyId: '', secretAccessKey: '', awsRegion: '' }}
 *   onChange={(field, value) => setFormData({ ...formData, [field]: value })}
 * />
 */
const AuthenticationFieldsRouter = ({
  mode,
  targetSystem,
  authMethod,
  formData,
  onChange,
  isAgentScannerGoDaddyEnabled
}) => {
  // Build the composite key for the registry lookup
  const formKey = `${targetSystem}:${authMethod}`;

  if (!isScenarioEnabled(formKey, isAgentScannerGoDaddyEnabled)) {
    return null;
  }

  // Look up the appropriate form component
  const FormComponent = FORM_REGISTRY[formKey];

  // If no form is registered for this combination, render nothing
  if (!FormComponent) {
    return null;
  }

  // Render the appropriate form component
  return <FormComponent mode={mode} formData={formData} onChange={onChange} />;
};

AuthenticationFieldsRouter.propTypes = {
  /** Form mode: 'add' or 'edit' */
  mode: PropTypes.oneOf(['add', 'edit']),
  /** The selected target system (e.g., 'amazon-bedrock', 'vertex-ai') */
  targetSystem: PropTypes.string,
  /** The selected authentication method (e.g., 'access-key', 'service-account') */
  authMethod: PropTypes.string,
  /** Current form data containing all field values */
  formData: PropTypes.object.isRequired,
  /** Callback to update form data: (fieldName, value) => void */
  onChange: PropTypes.func.isRequired,
  /** Feature flag gating for GoDaddy ANS */
  isAgentScannerGoDaddyEnabled: PropTypes.bool
};

AuthenticationFieldsRouter.defaultProps = {
  mode: 'add',
  targetSystem: '',
  authMethod: '',
  isAgentScannerGoDaddyEnabled: false
};

/**
 * Helper to check if a form exists for a given combination
 *
 * @param {string} targetSystem - Target system identifier
 * @param {string} authMethod - Authentication method identifier
 * @returns {boolean} True if a form is registered
 */
export const hasFormForScenario = (targetSystem, authMethod) => {
  const formKey = `${targetSystem}:${authMethod}`;

  return Boolean(FORM_REGISTRY[formKey]);
};

export const isScenarioVisible = (
  targetSystem,
  authMethod,
  isAgentScannerGoDaddyEnabled
) =>
  isScenarioEnabled(
    `${targetSystem}:${authMethod}`,
    isAgentScannerGoDaddyEnabled
  );

/**
 * Get all registered scenario keys
 *
 * @returns {string[]} Array of registered scenario keys
 */
export const getRegisteredScenarios = () => Object.keys(FORM_REGISTRY);

export default AuthenticationFieldsRouter;
